import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { skcHasColor } from '@/temu-platform/modules/products/components/SpecSupplySection';
import { resolvePublishSlotImageUrl } from '@/temu-platform/utils/temuPublishImageSlots';
import { appendCopywritingMaxCharsToPrompt } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { aiCopywritingAPI } from '@/utils/api';
import {
  clipTemuProductNameToLimit,
  TEMU_PRODUCT_NAME_DEFAULT_LIMIT,
} from '@/temu-platform/utils/temuProductNameLength';

/** @deprecated 请按一级类目使用 resolveTemuProductNameLimit；保留作兜底默认上限 */
export const TEMU_PUBLISH_TITLE_MAX_CHARS = TEMU_PRODUCT_NAME_DEFAULT_LIMIT;

const BASE_PROMPT =
  '根据商品主图，结合以下类目与商品属性，生成一条中文电商平台商品名称。要求：简体中文、简洁有吸引力、突出卖点，不要加引号或前缀。';

function normalizeGeneratedTitle(raw) {
  const text = String(raw || '').trim();
  return text.replace(/^(?:商品标题|产品标题|商品名称|标题)\s*[:：]\s*/i, '').trim();
}

function findAttributeValueName(attr, valueId) {
  const id = String(valueId || '').trim();
  if (!id) return '';
  const values = Array.isArray(attr?.values) ? attr.values : [];
  const hit = values.find((v) => String(v?.id) === id);
  return hit ? attributeValueDisplayLabel(hit) : '';
}

function resolveStoredAttributeValueText(attr, raw) {
  if (Array.isArray(raw)) {
    const parts = raw.map((row) => {
      if (row && typeof row === 'object') {
        const vid = String(row.valueId ?? row.attributeValueId ?? '').trim();
        const pct = String(row.percent ?? row.attributeExtraValue ?? '').trim();
        const name = findAttributeValueName(attr, vid) || vid;
        return pct ? `${name} ${pct}%` : name;
      }
      return findAttributeValueName(attr, row) || String(row || '').trim();
    }).filter(Boolean);
    return parts.join(', ');
  }
  if (raw && typeof raw === 'object') {
    const vid = String(raw.valueId ?? raw.attributeValueId ?? '').trim();
    const extra = String(raw.attributeExtraValue ?? raw.percent ?? '').trim();
    if (vid) {
      const name = findAttributeValueName(attr, vid) || vid;
      return extra ? `${name} ${extra}` : name;
    }
    return extra;
  }
  const text = String(raw ?? '').trim();
  return findAttributeValueName(attr, text) || text;
}

export function formatFilledProductAttributeLines(productAttributes = {}, attrDefs = []) {
  const attrs = Array.isArray(attrDefs) ? attrDefs : [];
  const lines = [];
  attrs.forEach((attr) => {
    const id = String(attr?.attributeId || '').trim();
    if (!id) return;
    const raw = productAttributes?.[id];
    if (raw == null || raw === '') return;
    const label = attributeDisplayLabel(attr) || id;
    const valueText = resolveStoredAttributeValueText(attr, raw);
    if (valueText) lines.push(`${label}: ${valueText}`);
  });
  return lines;
}

export function buildTemuPublishTitlePrompt({
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  maxChars = TEMU_PRODUCT_NAME_DEFAULT_LIMIT,
} = {}) {
  const lines = [];
  const category = String(categoryPathLabel || '').trim();
  if (category) lines.push(`类目：${category}`);
  const attrLines = formatFilledProductAttributeLines(productAttributes, attrDefs);
  if (attrLines.length) {
    lines.push('商品属性：');
    attrLines.forEach((line) => lines.push(`- ${line}`));
  }
  const context = lines.join('\n');
  const body = context ? `${BASE_PROMPT}\n\n${context}` : BASE_PROMPT;
  const limit = Number(maxChars) > 0 ? Number(maxChars) : TEMU_PRODUCT_NAME_DEFAULT_LIMIT;
  return appendCopywritingMaxCharsToPrompt(
    `${body}\n计权规则：字母/数字/英文标点计 1，其他字符（如中文）计 2。`,
    limit,
    TEMU_PRODUCT_NAME_DEFAULT_LIMIT,
  );
}

/** 第一个已选主规格颜色的主图 URL */
export function resolveFirstSkcMainImageUrl(skcList = []) {
  const list = Array.isArray(skcList) ? skcList : [];
  for (const skc of list) {
    if (!skcHasColor(skc)) continue;
    const url = resolvePublishSlotImageUrl(skc.mainImage);
    if (url) return url;
  }
  return '';
}

/**
 * 调用 AI 文案计费接口生成标题（按张扣 IPE 豆，与工作室 AI 文案同价）。
 */
export async function generateTemuPublishAiTitle({
  skcList = [],
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  maxChars = TEMU_PRODUCT_NAME_DEFAULT_LIMIT,
} = {}) {
  const imageUrl = resolveFirstSkcMainImageUrl(skcList);
  if (!imageUrl) {
    throw new Error('请先为第一个颜色上传主图');
  }

  const publicRef = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!publicRef) {
    throw new Error('主图无法解析，请重新上传');
  }

  const limit = Number(maxChars) > 0 ? Number(maxChars) : TEMU_PRODUCT_NAME_DEFAULT_LIMIT;
  const prompt = buildTemuPublishTitlePrompt({
    categoryPathLabel,
    productAttributes,
    attrDefs,
    maxChars: limit,
  });

  const result = await aiCopywritingAPI.submitAndWait({
    requestId: `temu-publish-title-${Date.now()}`,
    prompt,
    referenceUrls: [publicRef],
    outputCount: 1,
    language: 'zh',
    maxChars: limit,
  });

  const title = normalizeGeneratedTitle(result?.content || result?.text || '');
  if (!title) throw new Error('AI 未返回标题');

  return clipTemuProductNameToLimit(title, limit);
}

/**
 * 商品名称 → 英文名称（DeepSeek /listing-export/translate-title）。
 * 已是英文则原样返回。
 */
export async function translateTemuTitleToEnglish(title) {
  const text = String(title || '').trim();
  if (!text) return '';
  try {
    const { ensureEnglishListingTitle, titleContainsCjk } = await import(
      '@/pages/ipe/workbench/utils/ensureEnglishListingTitle'
    );
    if (!titleContainsCjk(text)) {
      // 纯英文/货号类：可直接作英文名
      return clipTemuProductNameToLimit(text, TEMU_PRODUCT_NAME_DEFAULT_LIMIT);
    }
    const en = await ensureEnglishListingTitle(text, {
      failMessage: '商品名称自动翻译成英文失败，请稍后重试',
    });
    return clipTemuProductNameToLimit(String(en || '').trim(), TEMU_PRODUCT_NAME_DEFAULT_LIMIT);
  } catch (error) {
    const msg = String(error?.message || '');
    if (/未能译成英文|不能包含中文|自动翻译成英文失败/.test(msg)) {
      throw new Error('商品名称自动翻译成英文失败，请稍后重试');
    }
    throw new Error(msg || '商品名称自动翻译成英文失败，请稍后重试');
  }
}
